"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildUnauthenticatedV2RequestConfig = exports.parseChallengeHeaders = exports.getToken = exports.paginatedV2CallWithLastMarker = exports.paginatedV2Call = exports.registryCall = exports.registryV2Call = void 0;
const parseLink = require("parse-link-header");
const url = require("url");
const needle_1 = require("./needle");
const BEARER_REALM = "Bearer realm";
const MAX_RETRIES = process.env.DOCKER_REGISTRY_V2_CLIENT_MAX_RETRIES
    ? parseInt(process.env.DOCKER_REGISTRY_V2_CLIENT_MAX_RETRIES, 10)
    : 1;
const MAX_REDIRECTS = 5;
async function registryV2Call(registryBase, endpoint, accept, callChainObserver, username, password, reqOptions = {}) {
    const reqConfig = buildUnauthenticatedV2RequestConfig(registryBase, endpoint, accept, reqOptions);
    try {
        callChainObserver.visitedUrls.push();
        return await (0, needle_1.needleWrapper)(reqConfig, MAX_RETRIES, callChainObserver);
    }
    catch (err) {
        if (err.statusCode === 401) {
            if (!username || !password) {
                // Supply and empty username and password if no credentials
                // are provided. These might be added later by a broker client.
                username = username ? username : "";
                password = password ? password : "";
            }
            const authConfig = await setAuthConfig(registryBase, err, reqConfig, username, password, reqOptions);
            try {
                return await (0, needle_1.needleWrapper)(authConfig, MAX_RETRIES, callChainObserver);
            }
            catch (err) {
                if (isRedirectStatus(err.statusCode)) {
                    return await handleRedirect(err, reqConfig, callChainObserver);
                }
                throw err;
            }
        }
        if (isRedirectStatus(err.statusCode)) {
            return await handleRedirect(err, reqConfig, callChainObserver);
        }
        throw err;
    }
}
exports.registryV2Call = registryV2Call;
/**
 * WARNING!!!
 *
 * This function was created for a very specific usecase (https://snyksec.atlassian.net/browse/MAGMA-1262)
 * It uses the existing mechanism of obtaining a token for authenticating, but can be used to hit any API endpoint,
 * and not necessarily a Docker V2 endpoint.
 * This is clearly an abuse of a library that's named after the v2 API, and this function should be considered a tech debt.
 * Once it's no longer necessary, it is advised that this function is removed.
 *
 */
async function registryCall(uri, username, password, reqOptions = {}, callChainObserver = { visitedUrls: [] }) {
    const reqConfig = applyRequestOptions({ uri: `https://${uri}` }, reqOptions);
    try {
        return await (0, needle_1.needleWrapper)(reqConfig, MAX_RETRIES, callChainObserver);
    }
    catch (err) {
        if (err.statusCode === 401) {
            if (!username || !password) {
                // Supply and empty username and password if no credentials
                // are provided. These might be added later by a broker client.
                username = username ? username : "";
                password = password ? password : "";
            }
            const authConfig = await setAuthConfig("", err, reqConfig, username, password, reqOptions);
            try {
                return await (0, needle_1.needleWrapper)(authConfig, MAX_RETRIES, callChainObserver);
            }
            catch (err) {
                if (isRedirectStatus(err.statusCode)) {
                    return await handleRedirect(err, reqConfig, callChainObserver);
                }
                throw err;
            }
        }
        if (isRedirectStatus(err.statusCode)) {
            return await handleRedirect(err, reqConfig, callChainObserver);
        }
        throw err;
    }
}
exports.registryCall = registryCall;
async function paginatedV2Call(registryBase, accept, username, password, endpoint, key, pageSize = 1000, maxPages = Number.MAX_SAFE_INTEGER, reqOptions = {}, callChainObserver = { visitedUrls: [] }) {
    const result = [];
    let pageEndpoint = `${endpoint}?n=${pageSize}`;
    let pageCount = 0;
    while (pageCount < maxPages) {
        const response = await registryV2Call(registryBase, pageEndpoint, accept, callChainObserver, username, password, reqOptions);
        const body = (0, needle_1.parseResponseBody)(response);
        if (body && body[key]) {
            result.push(...body[key]);
        }
        if (!response.headers.link) {
            break;
        }
        pageCount += 1;
        pageEndpoint = pageEndpointForLink(endpoint, response.headers.link);
    }
    return result;
}
exports.paginatedV2Call = paginatedV2Call;
/**
 * paginated Docker Registry v2 API call using "last" marker pagination.
 * this function correctly implements the Docker Registry v2 API pagination pattern
 * that uses the "last" parameter to indicate the last item from the previous response.
 * this allows for unbounded retrieval of resources without the traditional Link header limitations.
 * relevant docs: https://distribution.github.io/distribution/spec/api/#pagination-1
 */
async function paginatedV2CallWithLastMarker(registryBase, accept, username, password, endpoint, key, pageSize = 1000, reqOptions = {}, callChainObserver = { visitedUrls: [] }) {
    const result = [];
    let lastItem = undefined; // if this is undefined, we're on the first page so it has to be optional
    let shouldContinue = true;
    while (shouldContinue) {
        const queryParams = new URLSearchParams();
        queryParams.set("n", pageSize.toString());
        if (lastItem) {
            queryParams.set("last", lastItem);
        }
        const pageEndpoint = `${endpoint}?${queryParams.toString()}`;
        const response = await registryV2Call(registryBase, pageEndpoint, accept, callChainObserver, username, password, reqOptions);
        const body = (0, needle_1.parseResponseBody)(response);
        // Exit early if we don't have valid response data
        if (!body || !body[key] || !Array.isArray(body[key])) {
            shouldContinue = false;
            continue;
        }
        const items = body[key];
        result.push(...items);
        shouldContinue = items.length === pageSize;
        // set the last item for the next iteration (if there will be one)
        if (shouldContinue) {
            lastItem = items[items.length - 1];
        }
    }
    return result;
}
exports.paginatedV2CallWithLastMarker = paginatedV2CallWithLastMarker;
async function getToken(registryBase, authBase, service, scope, username, password, reqOptions = {}) {
    const reqConfig = applyRequestOptions({
        uri: authBase,
        qs: {
            service,
            scope,
        },
    }, Object.assign({}, reqOptions));
    // Test truthiness, should be false when username and password are undefined
    if (username && password) {
        reqConfig.username = username;
        reqConfig.password = password;
    }
    const response = await (0, needle_1.needleWrapper)(Object.assign(Object.assign({}, reqConfig), { 
        // we always want to read the credentials through the body
        snykInternalOutputStream: undefined }));
    const body = (0, needle_1.parseResponseBody)(response);
    return body.token || body.access_token;
}
exports.getToken = getToken;
function parseChallengeHeaders(challengeHeaders) {
    const headersMap = Object.create(null);
    const headerSplit = challengeHeaders.split(",");
    for (let i = 0; i < headerSplit.length; i++) {
        const entry = headerSplit[i];
        if (!entry.includes("=") && i > 0) {
            // we'll get here in case a value includes a comma. we want to concat what's after the comma to the previous value
            headerSplit[i - 1] += `,${entry}`;
            headerSplit.splice(i, 1);
            i--;
        }
    }
    headerSplit.forEach(entry => {
        if (!entry.includes("="))
            return;
        const [rawKey, ...valueParts] = entry.split("=");
        const key = rawKey.trim();
        const rawValue = valueParts.join("=").trim();
        if (rawValue.startsWith('"') && rawValue.endsWith('"')) {
            headersMap[key] = rawValue.slice(1, -1);
        }
        else {
            headersMap[key] = rawValue;
        }
    });
    return [headersMap[BEARER_REALM], headersMap.service, headersMap.scope];
}
exports.parseChallengeHeaders = parseChallengeHeaders;
function buildUnauthenticatedV2RequestConfig(registryBase, endpoint, accept, reqOptions = {}) {
    return applyRequestOptions({
        headers: { Accept: accept },
        uri: `https://${registryBase}/v2${endpoint}`,
    }, reqOptions);
}
exports.buildUnauthenticatedV2RequestConfig = buildUnauthenticatedV2RequestConfig;
function pageEndpointForLink(endpoint, link) {
    const linkPath = parseLink(link).next.url;
    const linkQuery = linkPath.split("?")[1];
    return `${endpoint}?${linkQuery}`;
}
async function setAuthConfig(registryBase, err, reqConfig, username, password, reqOptions) {
    // See: https://docs.docker.com/registry/spec/auth/token/#how-to-authenticate
    const challengeHeaders = err.headers["www-authenticate"];
    if (!challengeHeaders) {
        throw err;
    }
    const [authBase, service, scope] = parseChallengeHeaders(challengeHeaders);
    if (!authBase) {
        // basic auth
        return Object.assign(Object.assign({}, reqConfig), { username, password });
    }
    else {
        // bearer token
        const token = await getToken(registryBase, authBase, service, scope, username, password, reqOptions);
        return Object.assign(Object.assign({}, reqConfig), { headers: Object.assign(Object.assign({}, reqConfig.headers), { Authorization: `Bearer ${token}` }) });
    }
}
// Handle redirect while following up to MAX_REDIRECTS
async function handleRedirect(err, config, callChainObserver) {
    return handleRedirectRec(err, config, MAX_REDIRECTS, callChainObserver);
}
async function handleRedirectRec(err, config, maxRedirects = 0, callChainObserver) {
    if (maxRedirects === 0) {
        throw err;
    }
    // ACR does not handle redirects well, where automatic redirects
    // fail due to an unexpected authorization header.
    // the solution is to follow the redirect, however discarding
    // the token.
    const location = err.headers.location;
    if (!location) {
        throw err;
    }
    // check if artifactory is redirecting to landing page (deactivated instance, see #incident-2244-test-jfrog-artifactory-inactive for more details)
    if (location.includes("landing.jfrog.com")) {
        throw new Error(`Artifactory instance appears to be deactivated (redirected to ${location})`);
    }
    // Only clear the Authorization headers if the redirect is for
    // azure container registries.
    if (location.includes("azurecr.io")) {
        delete config.headers.Authorization;
    }
    const redirectUrl = assertFullUrl(location, config.uri);
    config.uri = redirectUrl;
    try {
        return await (0, needle_1.needleWrapper)(config, MAX_RETRIES, callChainObserver);
    }
    catch (err) {
        if (isRedirectStatus(err.statusCode)) {
            return await handleRedirectRec(err, config, maxRedirects - 1, callChainObserver);
        }
        throw err;
    }
}
/*
 * Takes request config and applies allowed options to it.
 * @param reqConfig - request config that is passed to the request library.
 * @param reqOptions - options passed in from outside of v2 client library.
 */
function applyRequestOptions(reqConfig, reqOptions) {
    const options = Object.assign({}, reqOptions);
    let uri = applyUriProtocol(reqConfig.uri, options.protocol);
    delete options.protocol;
    uri = applyUriHostMappings(uri, options.hostMappings);
    delete options.hostMappings;
    const headers = applyHeaders(reqConfig.headers, options.headers);
    delete options.headers;
    return Object.assign(Object.assign(Object.assign({}, reqConfig), options), { uri,
        headers });
}
function applyUriProtocol(uri, protocol) {
    if (!protocol) {
        return uri;
    }
    const updatedUrl = url.parse(uri);
    updatedUrl.protocol = protocol;
    return url.format(updatedUrl);
}
/**
 * Applies host mappings to given uri.
 *
 * @param uri
 * @param mappings - Array of mappings. Each mapping is represented as array
 *                   tuple: [host_regex_matcher, new_host].
 */
function applyUriHostMappings(uri, mappings) {
    if (!mappings) {
        return uri;
    }
    const updatedUrl = url.parse(uri);
    const mapping = mappings.find(([matcher]) => updatedUrl.host.match(matcher));
    if (!mapping) {
        return uri;
    }
    updatedUrl.host = mapping[1];
    return url.format(updatedUrl);
}
function applyHeaders(currentHeaders, addHeaders) {
    return Object.assign(Object.assign({}, (currentHeaders || {})), (addHeaders || {}));
}
function assertFullUrl(redirectLocation, originalLocation) {
    let redirectUrl;
    try {
        redirectUrl = new URL(redirectLocation);
    }
    catch (_a) {
        // in case the location is not a proper URL (contains just the path), attach the original URL's origin
        const originalUrl = new URL(originalLocation);
        redirectUrl = new URL(redirectLocation, originalUrl.origin);
    }
    return redirectUrl.href;
}
/*
 Check whether the response status requires redirect.
 Note: since we only make GET requests, we do not need to handle
 different redirect statuses having different requirements for
 handling the request method.
*/
function isRedirectStatus(statusCode) {
    return (statusCode === 301 ||
        statusCode === 302 ||
        statusCode === 303 ||
        statusCode === 307 ||
        statusCode === 308);
}
//# sourceMappingURL=registry-call.js.map